/**
 * Правая панель — «страница правды» одной сущности.
 *
 * Открывается из NarrativeBlockView, GlossarySidebar, План-борда, упоминаний
 * `Тип."Имя"` в чате/комментариях. Источник состояния — `ui-store`
 * (стек карточек: назад/вперёд как в браузере; deep-link через `?entity=`).
 *
 * Вкладки:
 *   Обзор      — что такое, исходник, сигналы, связи, срез
 *   Обсуждения — треды команды об этой сущности (GitHub Issues label
 *                pflow:comment: ответы, резолв). Ср. «Вопросы» в сайдбаре —
 *                проектный бэклог задач/идей, не привязанный к узлу.
 *   История    — git-история блока (GitLens-style)
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
    ActionIcon, Alert, Anchor, Avatar, Badge, Box, Button, Code, Collapse,
    Divider, Drawer, Group, Loader, ScrollArea, SegmentedControl, Stack, Table,
    Tabs, Text, Textarea, ThemeIcon, Tooltip, TypographyStylesProvider, UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
    IconAlertTriangle, IconArrowLeft, IconArrowRight, IconArrowUpRight,
    IconBuildingStore, IconCheck,
    IconChevronDown, IconCircleCheck, IconExternalLink, IconHistory,
    IconInfoCircle, IconLink, IconMessageCircle, IconRobot, IconX,
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { EntityRef } from '@pflow/shared/v2';
import {
    fetchEntitySource, fetchEntitySummary, fetchEntityBacklinks, fetchEntityHistory,
    fetchComments, fetchCommentThread, postComment, replyToComment,
    setCommentState,
    type EntityHistoryEntry, type BacklinkEntry, type EntityHealthFinding,
    type ForwardLinkEntry, type PflowComment,
} from '../lib/api-client';
import { linkifyMentionsInMarkdown, MentionAwareLink } from '../lib/entity-mentions';
import { useMe } from '../hooks/use-me';
import { useUiStore } from '../stores/ui-store';
import { useChatStore } from '../stores/chat-store';
import { CommentThread } from './CommentThread';
import { DiffView } from './EntityDiffCard';
import { DESKTOP_QUERY } from '../lib/breakpoints';

interface Props {
    owner:   string;
    repo:    string;
    gitRef?: string;
    sha?:    string;
    path?:   string;
}

export function EntityCard(props: Props): JSX.Element {
    const entity    = useUiStore(s => s.cardEntity);
    const close     = useUiStore(s => s.closeCard);
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true);
    // Открытый чат (правый сайдбар 400px): на десктопе карточка немодальна
    // и сдвигается влево от него — панель и чат видны ОДНОВРЕМЕННО
    // («спроси агента про эту сущность», глядя на её паспорт).
    const chatOpen  = useChatStore(s => s.open);

    // ── Deep-link: ?entity=Тип:Имя ────────────────────────────────────
    const [searchParams, setSearchParams] = useSearchParams();
    const openCard = useUiStore(s => s.openCard);

    // При загрузке страницы с ?entity= — открыть карточку.
    useEffect(() => {
        const raw = searchParams.get('entity');
        if (!raw || entity) return;
        const sep = raw.indexOf(':');
        if (sep <= 0) return;
        openCard({ type: raw.slice(0, sep), name: raw.slice(sep + 1) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Открытие/закрытие/навигация → синхронизировать URL (replace, без истории).
    useEffect(() => {
        const current = searchParams.get('entity');
        const wanted  = entity ? `${entity.type}:${entity.name}` : null;
        if (current === wanted) return;
        const next = new URLSearchParams(searchParams);
        if (wanted) next.set('entity', wanted); else next.delete('entity');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity]);

    return (
        <Drawer
            opened={entity !== null}
            onClose={close}
            position="right"
            size={isDesktop ? 'lg' : '100%'}
            withCloseButton
            title={entity && <DrawerTitle entity={entity} />}
            // Десктоп: немодальный режим — без оверлея и блокировки страницы,
            // можно читать карточку и параллельно писать в чат.
            withOverlay={!isDesktop}
            lockScroll={!isDesktop}
            trapFocus={!isDesktop}
            closeOnClickOutside={!isDesktop}
            zIndex={140}
            styles={{
                content: {
                    display: 'flex', flexDirection: 'column',
                    marginRight: isDesktop && chatOpen ? 400 : 0,
                    transition: 'margin-right 200ms ease',
                    boxShadow: 'var(--mantine-shadow-xl)',
                },
                body:    { flex: 1, padding: 0 },
                title:   { flex: 1, minWidth: 0 },
            }}
        >
            {entity && (
                <DrawerBody
                    key={`${entity.type}/${entity.name}/${entity.canvas ?? ''}`}
                    entity={entity}
                    owner={props.owner}
                    repo={props.repo}
                    gitRef={props.gitRef}
                    sha={props.sha}
                    path={props.path}
                />
            )}
        </Drawer>
    );
}

function DrawerTitle({ entity }: { entity: EntityRef }): JSX.Element {
    const { cardStack, cardIndex, backCard, forwardCard, requestChat } = useUiStore();
    const [copied, setCopied] = useState(false);
    const target = `${entity.type}."${entity.name}"`;

    const copyLink = (): void => {
        void navigator.clipboard.writeText(location.href).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            {cardStack.length > 1 && (
                <Group gap={0} wrap="nowrap">
                    <ActionIcon size="sm" variant="subtle" color="gray"
                        disabled={cardIndex <= 0} onClick={backCard} aria-label="Назад">
                        <IconArrowLeft size={15} />
                    </ActionIcon>
                    <ActionIcon size="sm" variant="subtle" color="gray"
                        disabled={cardIndex >= cardStack.length - 1} onClick={forwardCard} aria-label="Вперёд">
                        <IconArrowRight size={15} />
                    </ActionIcon>
                </Group>
            )}
            <Text fz="xs" c="dimmed" tt="uppercase" style={{ flexShrink: 0 }}>{entity.type}</Text>
            <Text fw={600} truncate style={{ minWidth: 0 }}>«{entity.name}»</Text>
            {entity.canvas && <Text fz="xs" c="dimmed" style={{ flexShrink: 0 }}>‹{entity.canvas}›</Text>}
            <Group gap={2} wrap="nowrap" ml="auto" style={{ flexShrink: 0 }}>
                <Tooltip label="Спросить агента об этой сущности" withArrow>
                    <ActionIcon size="sm" variant="subtle" color="blue"
                        onClick={() => requestChat(`Про ${target}: `)} aria-label="Спросить агента">
                        <IconRobot size={15} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label={copied ? 'Скопировано' : 'Ссылка на сущность'} withArrow>
                    <ActionIcon size="sm" variant="subtle" color="gray"
                        onClick={copyLink} aria-label="Скопировать ссылку">
                        {copied ? <IconCircleCheck size={15} /> : <IconLink size={15} />}
                    </ActionIcon>
                </Tooltip>
            </Group>
        </Group>
    );
}

// ─── Тело: вкладки ───────────────────────────────────────────────────────────

function DrawerBody({ entity, owner, repo, gitRef, sha, path }: Props & { entity: EntityRef }): JSX.Element {
    const [tab, setTab] = useState<string>('overview');
    const target = `${entity.type}."${entity.name}"`;

    const sourceQ = useQuery({
        queryKey: ['entity-source', owner, repo, gitRef, path, entity.type, entity.name],
        queryFn:  () => fetchEntitySource({ owner, repo, ref: gitRef, path, type: entity.type, name: entity.name }),
        retry:    false,
    });
    const backlinksQ = useQuery({
        queryKey: ['entity-backlinks', owner, repo, gitRef, path, entity.type, entity.name],
        queryFn:  () => fetchEntityBacklinks({ owner, repo, ref: gitRef, path, type: entity.type, name: entity.name }),
        retry:    false,
    });
    // Открытые обсуждения — для бейджа на вкладке (грузим сразу, это дёшево).
    // Поллинг здесь, т.к. DiscussionTab монтируется только на своей вкладке.
    const commentsQ = useQuery({
        queryKey: ['entity-comments', owner, repo, path, target, 'all'],
        queryFn:  () => fetchComments({ owner, repo, target, path, state: 'all' }),
        retry:    false,
        refetchInterval:      20_000,
        refetchOnWindowFocus: true,
    });
    const openCount = (commentsQ.data?.comments ?? []).filter(c => c.state === 'open').length;

    if (sourceQ.isLoading) return <Box p="md"><Loader size="sm" /></Box>;
    if (sourceQ.error) {
        return (
            <Box p="md">
                <Alert color="red" title="Не удалось загрузить исходник">
                    {(sourceQ.error as Error).message}
                </Alert>
            </Box>
        );
    }
    if (!sourceQ.data) return <Box p="md"><Text c="dimmed">Нет данных.</Text></Box>;

    const src          = sourceQ.data;
    const backlinks    = backlinksQ.data?.backlinks ?? [];
    const forwardLinks = backlinksQ.data?.forwardLinks ?? [];
    const docs         = backlinksQ.data?.docs ?? null;
    const market       = backlinksQ.data?.market ?? false;
    const linksCount   = backlinks.length + forwardLinks.length;

    return (
        <Tabs value={tab} onChange={v => setTab(v ?? 'overview')} keepMounted={false}
            styles={{ root: { display: 'flex', flexDirection: 'column', height: '100%' } }}>
            <Tabs.List px="md" style={{ flexShrink: 0 }}>
                <Tabs.Tab value="overview" fz="sm">Обзор</Tabs.Tab>
                <Tabs.Tab value="discuss" fz="sm"
                    rightSection={openCount > 0 ? <CountBadge n={openCount} color="blue" /> : undefined}>
                    Обсуждения
                </Tabs.Tab>
                <Tabs.Tab value="history" fz="sm">История</Tabs.Tab>
            </Tabs.List>

            <ScrollArea style={{ flex: 1 }} type="auto">
                <Tabs.Panel value="overview">
                    <OverviewTab entity={entity} src={src} backlinks={backlinks}
                        forwardLinks={forwardLinks} market={market}
                        docs={docs} owner={owner} repo={repo} gitRef={gitRef} path={path}
                        loadingLinks={backlinksQ.isLoading} />
                </Tabs.Panel>
                <Tabs.Panel value="discuss">
                    <DiscussionTab owner={owner} repo={repo} path={path} target={target} />
                </Tabs.Panel>
                <Tabs.Panel value="history">
                    <HistoryTab owner={owner} repo={repo} sha={sha} path={path} entity={entity} />
                </Tabs.Panel>
            </ScrollArea>
        </Tabs>
    );
}

function CountBadge({ n, color = 'gray' }: { n: number; color?: string }): JSX.Element {
    return <Badge size="xs" variant="light" color={color} circle={n < 10}>{n}</Badge>;
}

// ─── Обзор: эпистемический паспорт ───────────────────────────────────────────

interface SourceView {
    file:      string;
    range:     { start: { line: number } };
    blockText: string;
    summaryMd: string | null;
    health?:   EntityHealthFinding[];
}

/** `Статус: значение` из DSL-блока (первое вхождение). */
function extractStatus(blockText: string): string | null {
    const m = blockText.match(/^\s*Статус:\s*(\S+)/m);
    return m ? m[1] : null;
}

const STATUS_COLOR: Record<string, string> = {
    'гипотеза': 'gray', 'в_работе': 'blue', 'работает': 'green',
    'проблема': 'red', 'закрыто': 'gray',
    'идёт': 'blue', 'закрыта': 'gray', 'планируется': 'yellow',
    'открыта': 'yellow', 'принято': 'green',
    'активный': 'green', 'черновик': 'gray', 'завершён': 'gray',
    'опубликован': 'green',
    'перспективный': 'blue', 'действующий': 'green',
};

function OverviewTab({ entity, src, backlinks, forwardLinks, market, docs, owner, repo, gitRef, path, loadingLinks }: {
    entity: EntityRef; src: SourceView; backlinks: BacklinkEntry[];
    forwardLinks: ForwardLinkEntry[]; market: boolean;
    docs: string | null; owner: string; repo: string; gitRef?: string; path?: string;
    loadingLinks: boolean;
}): JSX.Element {
    const status = extractStatus(src.blockText);
    const linksCount = forwardLinks.length + backlinks.filter(b => b.sourceType !== 'Signal').length;

    const signals  = backlinks.filter(b => b.sourceType === 'Signal');
    const confirms = signals.filter(s => s.refItemType === 'SignalConfirms');
    const refutes  = signals.filter(s => s.refItemType === 'SignalRefutes');
    const related  = signals.filter(s => s.refItemType !== 'SignalConfirms' && s.refItemType !== 'SignalRefutes');

    return (
        <Box>
            {/* ── Плашка «рынок»: research-модель конкурента, не наша сущность ── */}
            {market && (
                <Alert color="orange" radius={0} variant="filled" py="xs"
                    icon={<IconBuildingStore size={16} />}
                    title="Рыночная сущность — не наша">
                    <Text fz="xs">
                        Research-модель конкурента (Принадлежность: рынок). Пустоты
                        здесь — «не видно снаружи», а не пробел нашей модели.
                    </Text>
                </Alert>
            )}

            {/* ── Статус-строка: быстрый взгляд «что за узел» ── */}
            {status && (
                <Group gap={8} px="md" py="sm" wrap="wrap"
                    style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Badge variant="light" color={STATUS_COLOR[status] ?? 'gray'}
                        style={{ textTransform: 'none' }}>
                        {status}
                    </Badge>
                </Group>
            )}

            {/* ── Проблемы: что чекеры говорят про ЭТОТ узел. Стоит высоко —
                   читать «что это» и не знать, что описание пустое, а
                   ссылок нет, значит читать половину правды. ────────────── */}
            {src.health && src.health.length > 0 && (
                <HealthSection findings={src.health} />
            )}

            {/* ── (1) Что такое <тип> ─────────────────────────────────── */}
            {docs && <DocsSection docs={docs} typeName={entity.type} />}

            {/* ── (2) Исходник (развёрнут по умолчанию) ────────────────── */}
            <SourceSection src={src} owner={owner} repo={repo} gitRef={gitRef} defaultOpen />

            {/* ── (3) Сигналы: чем узел подтверждён/оспорен ────────────── */}
            {loadingLinks && <Box px="md" py="sm"><Loader size="xs" /></Box>}
            {!loadingLinks && (signals.length > 0 || !market) && (
                <Box px="md" py="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Group gap={8} mb={signals.length > 0 ? 'xs' : 0} wrap="wrap">
                        <Text fz="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.5 }}>
                            Сигналы
                        </Text>
                        {confirms.length > 0 && (
                            <Badge size="sm" variant="light" color="green" leftSection={<IconCheck size={11} />}
                                style={{ textTransform: 'none' }}>за: {confirms.length}</Badge>
                        )}
                        {refutes.length > 0 && (
                            <Badge size="sm" variant="light" color="red" leftSection={<IconX size={11} />}
                                style={{ textTransform: 'none' }}>против: {refutes.length}</Badge>
                        )}
                        {signals.length === 0 && !market && (
                            <Badge size="sm" variant="outline" color="yellow" style={{ textTransform: 'none' }}>
                                без сигналов
                            </Badge>
                        )}
                    </Group>
                    {signals.length > 0 && (
                        <Stack gap={2}>
                            {[...refutes, ...confirms, ...related].map((s, i) => (
                                <Group key={i} gap={8} wrap="nowrap">
                                    <ThemeIcon size="xs" variant="light" radius="xl"
                                        color={s.refItemType === 'SignalRefutes' ? 'red' : s.refItemType === 'SignalConfirms' ? 'green' : 'gray'}>
                                        {s.refItemType === 'SignalRefutes' ? <IconX size={12} />
                                            : s.refItemType === 'SignalConfirms' ? <IconCheck size={12} />
                                            : <IconArrowRight size={12} />}
                                    </ThemeIcon>
                                    <EntityLinkName title={s.sourceTitle} name={s.sourceName} />
                                </Group>
                            ))}
                        </Stack>
                    )}
                </Box>
            )}

            {/* ── (4) Связи таблицей: исходящие + входящие (Премиссы/Развилки/
                   Гипотезы/Кейсы сгруппированы по типу источника) ───────── */}
            {linksCount > 0 && (
                <LinksSection forwardLinks={forwardLinks}
                    backlinks={backlinks.filter(b => b.sourceType !== 'Signal')} />
            )}

            {/* ── (5) Срез: LLM-пересказ сущности (кэш на репо); fallback —
                   структурный срез context-builder ────────────────────── */}
            {src.summaryMd && (
                <SummarySection entity={entity} fallbackMd={src.summaryMd}
                    owner={owner} repo={repo} gitRef={gitRef} path={path} />
            )}

            {/* ── (6) Обсудить с агентом. Срез и находки чекеров агент
                   получает сам (server кладёт их в system prompt), поэтому
                   кнопка несёт только вопрос пользователя. ─────────────── */}
            <DiscussWithAgent entity={entity} hasHealth={(src.health?.length ?? 0) > 0} />
        </Box>
    );
}

/** LLM-срез с fallback на структурный. Ленивая генерация: первый показ
 *  сущности может занять пару секунд, дальше — из кэша (общего для репо). */
function SummarySection({ entity, fallbackMd, owner, repo, gitRef, path }: {
    entity: EntityRef; fallbackMd: string;
    owner: string; repo: string; gitRef?: string; path?: string;
}): JSX.Element {
    const q = useQuery({
        queryKey: ['entity-summary', owner, repo, gitRef, path, entity.type, entity.name],
        queryFn:  () => fetchEntitySummary({ owner, repo, ref: gitRef, path, type: entity.type, name: entity.name }),
        retry:    false,
        staleTime: 5 * 60_000,
    });
    // LLM-текст, если сгенерировался; иначе структурный срез.
    const md = q.data?.text ?? (q.isError ? fallbackMd : null);

    return (
        <Box px="md" py="md" fz="sm" style={{ lineHeight: 1.55 }}>
            <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb="xs" style={{ letterSpacing: 0.5 }}>
                Срез
            </Text>
            {q.isLoading && (
                <Group gap={6}><Loader size={12} /><Text fz="xs" c="dimmed">Генерирую срез…</Text></Group>
            )}
            {!q.isLoading && (
                <TypographyStylesProvider fz="sm" style={{ overflowWrap: 'break-word' }}
                    styles={{ root: {
                        fontSize: 'var(--mantine-font-size-sm)',
                        '& > :first-child': { marginTop: 0 },
                        '& > :last-child':  { marginBottom: 0 },
                    } }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MentionAwareLink }}>
                        {linkifyMentionsInMarkdown(md ?? fallbackMd)}
                    </ReactMarkdown>
                </TypographyStylesProvider>
            )}
        </Box>
    );
}

/**
 * Находки чекеров по этой сущности. Показываем `message` целиком и
 * `fix.hint` под ним — «что не так» без «что делать» превращает панель в
 * упрёк. Заголовок чекера рядом бейджем: «Пустые описания» объясняет
 * находку быстрее, чем её собственный текст в отрыве от контекста.
 */
function HealthSection({ findings }: { findings: EntityHealthFinding[] }): JSX.Element {
    const critical = findings.filter(f => f.severity === 'critical').length;
    return (
        <Box px="md" py="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Group gap={8} mb="xs" wrap="wrap">
                <Text fz="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.5 }}>
                    Проблемы
                </Text>
                <Badge size="sm" variant="light" color={critical > 0 ? 'red' : 'yellow'}
                    style={{ textTransform: 'none' }}>
                    {findings.length}
                </Badge>
            </Group>
            <Stack gap={8}>
                {findings.map((f, i) => (
                    <Box key={i}>
                        <Group gap={6} wrap="nowrap" align="flex-start">
                            <ThemeIcon size="xs" variant="light" radius="xl"
                                color={f.severity === 'critical' ? 'red' : 'yellow'} mt={2}>
                                <IconAlertTriangle size={11} />
                            </ThemeIcon>
                            <Box style={{ minWidth: 0, flex: 1 }}>
                                <Text fz="sm" style={{ wordBreak: 'break-word' }}>{f.message}</Text>
                                {f.fix?.hint && (
                                    <Text fz="xs" c="dimmed" mt={2} style={{ wordBreak: 'break-word' }}>
                                        {f.fix.hint}
                                    </Text>
                                )}
                                <Text fz="xs" c="dimmed" mt={2} fs="italic">{f.aspectTitle}</Text>
                            </Box>
                        </Group>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

/**
 * «Обсудить с агентом» в конце «Обзора». Prefill — только вопрос: сам
 * срез и находки чекеров сервер кладёт агенту в system prompt по
 * `pageContext.openEntity`, поэтому дублировать текст в сообщение не
 * нужно (и не хочется — он длинный).
 */
function DiscussWithAgent({ entity, hasHealth }: {
    entity: EntityRef; hasHealth: boolean;
}): JSX.Element {
    const requestChat = useUiStore(s => s.requestChat);
    const ask = (): void => {
        const ref = `${entity.type}."${entity.name}"`;
        requestChat(hasHealth
            ? `Обсудим ${ref}. Чекеры нашли проблемы — с чего начать и что это меняет для модели?\n\nМой вопрос: `
            : `Обсудим ${ref}.\n\nМой вопрос: `);
    };
    return (
        <Box px="md" pb="md">
            <Button variant="light" size="xs" leftSection={<IconRobot size={14} />}
                onClick={ask} fullWidth>
                Обсудить с агентом
            </Button>
            <Text fz="xs" c="dimmed" mt={6} ta="center">
                Агент получит этот срез и находки чекеров в контекст.
            </Text>
        </Box>
    );
}

/** Свёрнутая секция «Связи»: исходящие + входящие таблицами. */
function LinksSection({ forwardLinks, backlinks }: {
    forwardLinks: ForwardLinkEntry[]; backlinks: BacklinkEntry[];
}): JSX.Element {
    const [open, setOpen] = useState(false);
    const n = forwardLinks.length + backlinks.length;
    return (
        <Box style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <UnstyledButton onClick={() => setOpen(o => !o)}
                style={{ display: 'block', width: '100%', padding: '8px 16px' }}>
                <Group gap={6} wrap="nowrap">
                    <IconLink size={14} color="var(--mantine-color-dimmed)" />
                    <Text fz="sm" c="dimmed">Связи таблицей</Text>
                    <Text fz="xs" c="dimmed">({n})</Text>
                    <IconChevronDown size={13} style={{
                        marginLeft: 'auto', color: 'var(--mantine-color-dimmed)',
                        transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease',
                    }} />
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                {forwardLinks.length > 0 && <ForwardLinksSection links={forwardLinks} />}
                {backlinks.length > 0 && <BacklinksSection backlinks={backlinks} />}
            </Collapse>
        </Box>
    );
}

// ─── Исходник ────────────────────────────────────────────────────────────────

function SourceSection({ src, owner, repo, gitRef, defaultOpen = false }: {
    src: SourceView; owner: string; repo: string; gitRef?: string; defaultOpen?: boolean;
}): JSX.Element {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <UnstyledButton onClick={() => setOpen(o => !o)}
                style={{ display: 'block', width: '100%', padding: '8px 16px' }}>
                <Group gap={6} wrap="nowrap">
                    <Text fz="sm" c="dimmed">DSL-исходник</Text>
                    <Text fz="xs" c="dimmed" ff="monospace" truncate style={{ minWidth: 0 }}>
                        {src.file}:{src.range.start.line + 1}
                    </Text>
                    <IconChevronDown size={13} style={{
                        marginLeft: 'auto', color: 'var(--mantine-color-dimmed)',
                        transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease',
                    }} />
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Group justify="flex-end" px="md">
                    <Anchor href={githubUrl(owner, repo, gitRef, src.file, src.range.start.line + 1)}
                        target="_blank" rel="noreferrer" fz="xs">
                        <Group gap={4} wrap="nowrap"><Text fz="xs">GitHub</Text><IconExternalLink size={12} /></Group>
                    </Anchor>
                </Group>
                <Code block style={{
                    margin: 0, background: 'transparent', whiteSpace: 'pre',
                    fontSize: 13, lineHeight: 1.5, padding: 16, paddingTop: 4,
                }}>
                    {src.blockText}
                </Code>
            </Collapse>
        </Box>
    );
}

// ─── Справка ─────────────────────────────────────────────────────────────────

function DocsSection({ docs, typeName }: { docs: string; typeName: string }): JSX.Element {
    const [open, setOpen] = useState(false);
    return (
        <Box style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <UnstyledButton onClick={() => setOpen(o => !o)}
                style={{ display: 'block', width: '100%', padding: '8px 16px' }}>
                <Group gap={6} wrap="nowrap">
                    <IconInfoCircle size={14} color="var(--mantine-color-blue-6)" />
                    <Text fz="sm" c="blue.6">Что такое {typeName}?</Text>
                    <IconChevronDown size={13} style={{
                        marginLeft: 'auto', color: 'var(--mantine-color-dimmed)',
                        transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease',
                    }} />
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Box px="md" pb="md">
                    <TypographyStylesProvider fz="sm" style={{ overflowWrap: 'break-word' }}
                        styles={{ root: {
                            fontSize: 'var(--mantine-font-size-sm)',
                            '& > :first-child': { marginTop: 0 },
                            '& > :last-child':  { marginBottom: 0 },
                        } }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{docs}</ReactMarkdown>
                    </TypographyStylesProvider>
                </Box>
            </Collapse>
        </Box>
    );
}

// ─── Связи ───────────────────────────────────────────────────────────────────

function EntityLinkName({ title, name }: { title: string; name: string }): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    return (
        <UnstyledButton
            onClick={() => openCard({ type: title, name })}
            title={`Открыть «${name}»`}
            style={{ display: 'block', maxWidth: '100%' }}
        >
            <Text fz="xs" fw={500} c="blue.6" style={{ wordBreak: 'break-word' }}>
                {name}
            </Text>
        </UnstyledButton>
    );
}

function ForwardLinksSection({ links }: { links: ForwardLinkEntry[] }): JSX.Element {
    return (
        <Box px="md" py="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Group gap={6} mb="xs" wrap="nowrap">
                <IconArrowUpRight size={14} color="var(--mantine-color-dimmed)" />
                <Text fz="sm" fw={600} c="dimmed">Ссылается на</Text>
                <Text fz="xs" c="dimmed">({links.length})</Text>
            </Group>
            <Table fz="xs" withRowBorders={false} verticalSpacing={3}>
                <Table.Tbody>
                    {links.map((l, i) => (
                        <Table.Tr key={i}>
                            <Table.Td c="dimmed" w={80} style={{ whiteSpace: 'nowrap' }}>{l.targetTitle}</Table.Td>
                            <Table.Td style={{ wordBreak: 'break-word' }}>
                                <EntityLinkName title={l.targetTitle} name={l.targetName} />
                            </Table.Td>
                            <Table.Td c="dimmed" style={{ whiteSpace: 'nowrap' }}>{l.relation}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Box>
    );
}

/** Входящие ссылки, сгруппированные по типу источника — «кто и как использует». */
function BacklinksSection({ backlinks }: { backlinks: BacklinkEntry[] }): JSX.Element {
    const groups = useMemo(() => {
        const m = new Map<string, BacklinkEntry[]>();
        for (const b of backlinks) {
            const list = m.get(b.sourceTitle) ?? [];
            list.push(b);
            m.set(b.sourceTitle, list);
        }
        return [...m.entries()];
    }, [backlinks]);

    if (backlinks.length === 0) return <></>;
    return (
        <Box px="md" py="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Group gap={6} mb="xs" wrap="nowrap">
                <IconLink size={14} color="var(--mantine-color-dimmed)" />
                <Text fz="sm" fw={600} c="dimmed">Используется в</Text>
                <Text fz="xs" c="dimmed">({backlinks.length})</Text>
            </Group>
            <Stack gap="xs">
                {groups.map(([title, items]) => (
                    <Box key={title}>
                        <Text fz="xs" fw={600} c="dimmed" mb={2}>{title} · {items.length}</Text>
                        <Table fz="xs" withRowBorders={false} verticalSpacing={2}>
                            <Table.Tbody>
                                {items.map((b, i) => (
                                    <Table.Tr key={i}>
                                        <Table.Td style={{ wordBreak: 'break-word' }}>
                                            <EntityLinkName title={b.sourceTitle} name={b.sourceName} />
                                        </Table.Td>
                                        <Table.Td c="dimmed" w={120} style={{ whiteSpace: 'nowrap' }}>{b.relation}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

// ─── Обсуждение: треды ───────────────────────────────────────────────────────

function DiscussionTab({ owner, repo, path, target }: {
    owner: string; repo: string; path?: string; target: string;
}): JSX.Element {
    const meQ = useMe();
    const isAuthed = !!meQ.data?.user;
    const queryClient = useQueryClient();
    const [draft, setDraft]   = useState('');
    const [filter, setFilter] = useState<'open' | 'all'>('open');

    const commentsKey = ['entity-comments', owner, repo, path, target, 'all'] as const;
    const commentsQ = useQuery({
        queryKey: commentsKey,
        queryFn:  () => fetchComments({ owner, repo, target, path, state: 'all' }),
        retry:    false,
        // Поллинг: чужие комментарии подтягиваются, пока карточка открыта.
        refetchInterval:      20_000,
        refetchOnWindowFocus: true,
    });

    const invalidate = (): void => {
        void queryClient.invalidateQueries({ queryKey: ['entity-comments', owner, repo, path, target, 'all'] });
        void queryClient.invalidateQueries({ queryKey: ['all-comments', owner, repo, path] });
    };

    const postM = useMutation({
        mutationFn: () => postComment({ owner, repo, target, text: draft.trim(), path }),
        onSuccess:  (res) => {
            setDraft('');
            // Оптимистично: GitHub issue-list консистентен не мгновенно, а
            // серверный ответ уже содержит созданный комментарий — показываем
            // сразу, затем сверяемся с сервером.
            queryClient.setQueryData<{ comments: PflowComment[] }>(commentsKey, prev =>
                prev ? { comments: [...prev.comments, res.comment] } : { comments: [res.comment] });
            void queryClient.invalidateQueries({ queryKey: ['all-comments', owner, repo, path] });
            setTimeout(invalidate, 3000);
        },
    });

    const all      = commentsQ.data?.comments ?? [];
    const visible  = filter === 'open' ? all.filter(c => c.state === 'open') : all;
    const resolved = all.length - all.filter(c => c.state === 'open').length;

    return (
        <Box px="md" py="md">
            <Group justify="space-between" mb="sm" wrap="nowrap">
                <Group gap={6} wrap="nowrap">
                    <IconMessageCircle size={14} color="var(--mantine-color-dimmed)" />
                    <Text fz="sm" fw={600} c="dimmed">Обсуждения</Text>
                </Group>
                {resolved > 0 && (
                    <SegmentedControl size="xs" value={filter}
                        onChange={v => setFilter(v as 'open' | 'all')}
                        data={[
                            { value: 'open', label: 'Открытые' },
                            { value: 'all',  label: `Все (${all.length})` },
                        ]} />
                )}
            </Group>

            {commentsQ.isLoading && <Loader size="xs" />}
            {!commentsQ.isLoading && visible.length === 0 && (
                <Text fz="xs" c="dimmed" mb="sm">
                    {filter === 'open' && resolved > 0 ? 'Все обсуждения решены.' : 'Пока нет — начни обсуждение.'}
                </Text>
            )}

            <Stack gap="sm" mb={visible.length > 0 ? 'md' : 0}>
                {visible.map(c => (
                    <CommentThread key={c.id} owner={owner} repo={repo} comment={c}
                        isAuthed={isAuthed} onChanged={invalidate} />
                ))}
            </Stack>

            {isAuthed ? (
                <Stack gap={6}>
                    <Textarea
                        placeholder="Новое обсуждение… (уйдёт в GitHub Issue от твоего имени)"
                        autosize minRows={2} size="xs"
                        value={draft}
                        onChange={e => setDraft(e.currentTarget.value)}
                    />
                    <Group justify="flex-end" gap={8}>
                        {postM.isError && <Text fz="xs" c="red.7">{(postM.error as Error).message}</Text>}
                        <Button size="xs" variant="light"
                            disabled={draft.trim().length === 0}
                            loading={postM.isPending}
                            onClick={() => postM.mutate()}>
                            Начать обсуждение
                        </Button>
                    </Group>
                </Stack>
            ) : (
                <Text fz="xs" c="dimmed">Войди через GitHub, чтобы комментировать.</Text>
            )}
        </Box>
    );
}

// ─── История ─────────────────────────────────────────────────────────────────

function HistoryTab({ owner, repo, sha, path, entity }: {
    owner: string; repo: string; sha?: string; path?: string; entity: EntityRef;
}): JSX.Element {
    const { data, isLoading, error } = useQuery({
        queryKey: ['entity-history', owner, repo, sha, path, entity.type, entity.name],
        queryFn:  () => fetchEntityHistory({ owner, repo, sha: sha!, path, type: entity.type, name: entity.name, limit: 12 }),
        enabled:  !!sha,
        retry:    false,
    });

    return (
        <Box px="md" py="md">
            <Group gap={6} wrap="nowrap" mb="xs">
                <IconHistory size={14} />
                <Text fw={600} fz="sm">История изменений</Text>
            </Group>
            {!sha && <Text fz="sm" c="dimmed">Недоступно для этой версии.</Text>}
            {isLoading && <Loader size="sm" />}
            {error && (
                <Alert color="red" title="Не удалось загрузить историю">
                    {(error as Error).message}
                </Alert>
            )}
            {data && data.history.length === 0 && (
                <Text fz="sm" c="dimmed">Изменений не найдено.</Text>
            )}
            {data && data.history.length > 0 && (
                <Stack gap={4}>
                    {data.history.map((entry, i) => (
                        <HistoryRow key={entry.sha} owner={owner} repo={repo}
                            entry={entry} prev={data.history[i + 1]?.source ?? ''} />
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function HistoryRow({ owner, repo, entry, prev }: {
    owner: string; repo: string; entry: EntityHistoryEntry; prev: string;
}): JSX.Element {
    const [open, setOpen] = useState(false);
    return (
        <Box style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 4 }}>
            <UnstyledButton onClick={() => setOpen(o => !o)} aria-expanded={open}
                style={{ display: 'block', width: '100%', padding: '6px 10px' }}>
                <Group justify="space-between" wrap="nowrap" gap={8}>
                    <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text fz="sm" truncate>{entry.message || '(без сообщения)'}</Text>
                        <Text fz="xs" c="dimmed">{entry.author} · {formatDate(entry.date)}</Text>
                    </Box>
                    <IconChevronDown size={14} style={{
                        flexShrink: 0, color: 'var(--mantine-color-dimmed)',
                        transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease',
                    }} />
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Divider />
                <Box p={8}>
                    <Group justify="flex-end" mb={6}>
                        <Anchor href={`https://github.com/${owner}/${repo}/commit/${entry.sha}`}
                            target="_blank" rel="noreferrer" fz="xs">
                            <Group gap={4} wrap="nowrap">
                                <Text fz="xs" ff="monospace">{entry.sha.slice(0, 7)}</Text>
                                <IconExternalLink size={12} />
                            </Group>
                        </Anchor>
                    </Group>
                    {entry.source === null ? (
                        <Text fz="sm" c="dimmed">Срез блока недоступен для этого коммита.</Text>
                    ) : (
                        <DiffView before={prev} after={entry.source} />
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function githubUrl(owner: string, repo: string, ref: string | undefined, file: string, line: number): string {
    return `https://github.com/${owner}/${repo}/blob/${ref ?? 'main'}/${file}#L${line}`;
}

function formatDate(iso: string): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toISOString().slice(0, 10);
    } catch {
        return iso;
    }
}
